/* eslint-disable react/require-default-props */
import React, { useCallback, useRef } from 'react';
import { AIInputProvider, useAIInput } from './AIInputContext';
import { CleanTextInput } from './components/CleanTextInput';
import { AttachmentList } from './components/AttachmentList';
import { FooterActions } from './components/FooterActions';
import './AIInputStyles.scss';
/** Dead-zone after a send during which a repeated submit is ignored (ms). */
const SUBMIT_DEBOUNCE_MS = 700;
/**
 * Inner component that consumes the AIInput context
 */
function AIInputInner({ placeholder, disabled = false, isLoading = false, allowedFormats, autoFocus, textareaRef, enableVoiceRecording = false, allowAttachments = true, onSubmit, onStop }) {
    const { text, attachments, clearAll } = useAIInput();
    const hasText = text.trim().length > 0;
    // Double-click / debounce guard for send. There is a brief window between
    // firing onSubmit and the parent flipping isLoading (which turns this button
    // into Stop); a fast second click in that window could double-send or — worse —
    // land on the now-Stop button and cancel the message the user just sent.
    // Ignore submits fired within the dead-zone.
    const lastSubmitAtRef = useRef(0);
    const handleContainerClick = useCallback((e) => {
        var _a;
        const target = e.target;
        if (target.closest('button, a, input, [role="button"]'))
            return;
        (_a = textareaRef === null || textareaRef === void 0 ? void 0 : textareaRef.current) === null || _a === void 0 ? void 0 : _a.focus();
    }, [textareaRef]);
    const handleSubmit = useCallback(() => {
        // Always require text — file-only submissions are not supported yet
        if (disabled || !hasText)
            return;
        const now = Date.now();
        if (now - lastSubmitAtRef.current < SUBMIT_DEBOUNCE_MS)
            return;
        lastSubmitAtRef.current = now;
        onSubmit({
            text: text.trim(),
            files: attachments.map((a) => a.file)
        });
        clearAll();
    }, [disabled, text, attachments, hasText, onSubmit, clearAll]);
    return (React.createElement("div", { className: "qmb-ui-ai-input", onClick: handleContainerClick, role: "presentation" },
        React.createElement(AttachmentList, null),
        React.createElement(CleanTextInput, { placeholder: placeholder, onSubmit: handleSubmit, autoFocus: autoFocus, textareaRef: textareaRef }),
        React.createElement(FooterActions, { enableVoiceRecording: enableVoiceRecording, disabled: disabled || !hasText, allowedFormats: allowedFormats, isLoading: isLoading, allowAttachments: allowAttachments, onSubmit: handleSubmit, onStop: onStop })));
}
/**
 * AIInput Organism
 * A rich input form with text input, file attachments, and action buttons.
 * Wraps all sub-components in an AIInputProvider for shared state.
 * @version 1.0.0
 * @since 2026-02-17
 */
export function AIInput({ placeholder, defaultValue, disabled = false, isLoading = false, allowedFormats, maxFiles = 10, enableVoiceRecording = false, allowAttachments = true, onSubmit, onStop, autoFocus, textareaRef }) {
    return (React.createElement(AIInputProvider, { allowedFormats: allowedFormats, maxFiles: maxFiles, defaultValue: defaultValue },
        React.createElement(AIInputInner, { placeholder: placeholder, disabled: disabled, isLoading: isLoading, allowedFormats: allowedFormats, autoFocus: autoFocus, textareaRef: textareaRef, enableVoiceRecording: enableVoiceRecording, allowAttachments: allowAttachments, onSubmit: onSubmit, onStop: onStop })));
}
